import { AngelRightIcon, BlueCheckIcon, Notification } from '~/svgs/common';
import Input, { PhonePicker } from 'components/common/input';
import { Translate, useTranslate } from '~/i18n';
import { useEffect, useState } from 'react';

import Axios from '~/utils/axios';
import Button from '~/components/common/button';
import Link from 'next/link';
import Loading from 'components/common/loaders/loader';
import { UserType } from '~/interfaces/user';
import classNames from 'classnames';
import { sentryCaptureException } from '~/errors/sentry';
import { useAuth } from '~/auth';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';

type SubmitObject = {
   country_code: string;
   mobile: string;
   venue_id: string;
   name: string;
   email: string;
};

interface OfflineFormProps {
   venue_id: string;
   closeModal?: () => void;
}

const OfflineForm = ({ venue_id }: OfflineFormProps) => {
   const { translate, lang } = useTranslate();
   const { user, authenticated } = useAuth<UserType>();
   const [loading, setLoading] = useState(false);
   const [mobileError, setMobileError] = useState('');
   const [notificationEnabled, setNotificationEnabled] = useState(true);
   const { executeRecaptcha } = useGoogleReCaptcha();
   const [success, setSuccess] = useState<boolean>(false);
   const router = useRouter();
   const {
      register,
      handleSubmit,
      getValues,
      formState: { errors },
      setValue,
   } = useForm<SubmitObject>({
      mode: 'onChange',
      defaultValues: {
         country_code: '966',
         name: user?.name,
         email: user?.email,
         mobile: user?.cell_number,
      },
   });
   const values = getValues();

   const submitHandler = async ({ country_code, mobile, name, email }: SubmitObject) => {
      if (loading) return;
      setLoading(true);
      try {
         const captcha = await executeRecaptcha?.();
         const { data } = await Axios.post('/v2/booking_interest', {
            country_code: country_code || 'null',
            mobile: user && user?.cell_number ? user?.cell_number : mobile,
            name,
            email,
            venue_id,
            is_user: authenticated,
            user_id: user?.user_id,
            captcha,
            source: 'is_madinah',
         });

         if (data.status == 'Success' || data.original?.status === 'Success') {
            setLoading(false);
            setSuccess(true);
            // closeModal();
         } else {
            setMobileError(data.error.cell_number);
            setLoading(false);
            setSuccess(false);
            throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error) {
         console.info(error);
         setLoading(false);
         setSuccess(false);
         sentryCaptureException(error);
      }
   };

   useEffect(() => {
      register('country_code', {
         required: translate({ id: 'auth:required field' }),
      });
   }, [register, translate]);

   return (
      <div>
         {!success ? (
            <>
               <div className="pb-2 text-2xl font-semibold">
                  <Translate id="booking:experience is offline" />
               </div>
               <div className="pb-4 text-sm">
                  <Translate parseHTML id="booking:offline experience description" />
               </div>
               <form onSubmit={handleSubmit(submitHandler)}>
                  {!user || !user.cell_number ? (
                     <div className="w-full mb-6">
                        <Input
                           label={<Translate id="booking:your name" />}
                           labelClassName="text-sm"
                           id="name"
                           error={errors.name?.message}
                           className="disabled:bg-gray-light border-primary-disabled"
                           {...register('name', {
                              required: translate({ id: 'auth:required field' }),
                           })}
                        />
                        <Input
                           label={<Translate id="booking:your email" />}
                           labelClassName="text-sm"
                           id="email"
                           type="email"
                           error={errors.email?.message}
                           className="disabled:bg-gray-light border-primary-disabled"
                           {...register('name', {
                              required: translate({ id: 'auth:required field' }),
                           })}
                        />
                        <Input
                           type="number"
                           label={translate({ id: 'auth:enter number' })}
                           className="appearance-none rounded-default"
                           groupClassName=""
                           placeholder="XXXXXXXXX"
                           id="mobile"
                           inputMode="numeric"
                           autoCorrect="off"
                           autoComplete="none"
                           autoSave="off"
                           inputWrapperClassName="relative"
                           {...register('mobile', {
                              required: translate({ id: 'auth:required field' }),
                           })}
                           error={mobileError}
                           valid={!!values.mobile}
                           prependClassName={classNames(
                              !!errors.country_code && 'border-primary-error',
                              !!values.country_code && 'border-primary-success'
                           )}
                           prepend={
                              <div className="w-12 h-full">
                                 <PhonePicker
                                    placeholder="XXX"
                                    onCountrySelected={({ phone }) =>
                                       setValue('country_code', phone, {
                                          shouldValidate: true,
                                       })
                                    }
                                 />
                              </div>
                           }
                        />
                     </div>
                  ) : null}

                  <div>
                     <Input
                        type="checkbox"
                        className="items-center mb-4 focus:outline-none"
                        name="notification_status"
                        label={translate({ id: 'booking:offline experience alert me label' })}
                        defaultChecked={notificationEnabled}
                        onChange={() => setNotificationEnabled(!notificationEnabled)}
                     />
                  </div>

                  <Button
                     btnType="submit"
                     id="mobile-login"
                     className={classNames('w-full h-12 mt-5 ', 'bg-madinah-primary')}
                     disabled={(user ? false : loading || !values.mobile) || !notificationEnabled}>
                     {loading ? (
                        <Loading color="white" />
                     ) : (
                        <span className="flex items-center justify-center gap-2 ">
                           <Notification />
                           <Translate id="booking:notify me" />
                        </span>
                     )}
                  </Button>

                  <div className="mt-4 text-sm text-center text-gray-500">
                     <Translate id="booking:offline booking terms and conditions" />{' '}
                     <Link
                        href={`/${lang}/terms-and-conditions`}
                        className="cursor-pointer text-madinah-primary hover:text-madinah-primary-hover hover:underline">
                        <Translate id="booking:learn more here" />
                     </Link>
                  </div>
               </form>
            </>
         ) : (
            <>
               <BlueCheckIcon />
               <div className="pb-2 mt-4 text-2xl font-semibold">
                  <Translate id="booking:you will be notified once the experience is available again" />
               </div>
               <div className="pb-8 text-sm">
                  <Translate parseHTML id="booking:book offline sucess message description" />
               </div>
               <div className="pb-6 text-sm font-semibold">
                  <Translate id="booking:check our experiences" />
               </div>

               <Button
                  className={classNames(
                     'w-full h-12 bg-gray-100 flex items-center gap-2 justify-center'
                  )}
                  type="secondary"
                  onClick={() => router.push(`/`)}>
                  <Translate id="booking:browse experiences" />
                  <AngelRightIcon className="w-4 h-4" />
               </Button>

               <div className="mt-4 text-sm text-center text-gray-500">
                  <Translate id="booking:offline booking terms and conditions" />{' '}
                  <Link
                     href={`/${lang}/terms-and-conditions`}
                     className="cursor-pointer text-madinah-primary hover:text-madinah-primary-hover hover:underline">
                     <Translate id="booking:learn more here" />
                  </Link>
               </div>
            </>
         )}
      </div>
   );
};

export default OfflineForm;
